import { useState } from 'react';
import { Wifi, WifiOff, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { ConnectionState } from '../hooks/useWebSocket';

interface ConnectionStatusProps {
  connectionState: ConnectionState;
  lastConnectedAt: Date | null;
  onReconnect: () => void;
}

export function ConnectionStatus({
  connectionState,
  lastConnectedAt,
  onReconnect,
}: ConnectionStatusProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  const getStatusConfig = () => {
    switch (connectionState) {
      case 'connected':
        return {
          icon: Wifi,
          color: 'text-success',
          bgColor: 'bg-success/10',
          label: 'Connected',
          description: 'Real-time updates active',
        };
      case 'connecting':
        return {
          icon: Loader2,
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          label: 'Connecting',
          description: 'Establishing connection...',
          animate: true,
        };
      case 'disconnected':
        return {
          icon: WifiOff,
          color: 'text-foreground-muted',
          bgColor: 'bg-background-muted',
          label: 'Disconnected',
          description: 'Attempting to reconnect...',
        };
      case 'error':
        return {
          icon: AlertCircle,
          color: 'text-danger',
          bgColor: 'bg-danger/10',
          label: 'Connection Error',
          description: 'Click to retry',
        };
      case 'unauthenticated':
        return {
          icon: AlertCircle,
          color: 'text-danger',
          bgColor: 'bg-danger/10',
          label: 'Session Expired',
          description: 'Please refresh the page to sign in again',
        };
      default:
        return {
          icon: WifiOff,
          color: 'text-foreground-muted',
          bgColor: 'bg-background-muted',
          label: 'Unknown',
          description: 'Unknown connection state',
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <div className="relative">
      <button
        onClick={connectionState === 'error' || connectionState === 'unauthenticated' ? onReconnect : undefined}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={`
          flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium
          ${config.bgColor} ${config.color}
          ${connectionState === 'error' || connectionState === 'unauthenticated' ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}
          transition-colors
        `}
        aria-label={`Connection status: ${config.label}`}
      >
        <Icon
          className={`h-3.5 w-3.5 ${config.animate ? 'animate-spin' : ''}`}
        />
        <span className="hidden sm:inline">{config.label}</span>
      </button>

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute right-0 top-full mt-2 z-50">
          <div className="bg-background border border-border rounded-lg shadow-lg p-3 min-w-[200px]">
            <div className="flex items-center gap-2 mb-2">
              <Icon className={`h-4 w-4 ${config.color}`} />
              <span className="font-medium text-foreground">{config.label}</span>
            </div>
            <p className="text-xs text-foreground-muted mb-2">
              {config.description}
            </p>
            {lastConnectedAt && connectionState === 'connected' && (
              <p className="text-xs text-foreground-muted">
                Connected {formatDistanceToNow(lastConnectedAt, { addSuffix: true })}
              </p>
            )}
            {(connectionState === 'error' || connectionState === 'unauthenticated') && (
              <button
                onClick={connectionState === 'unauthenticated' ? () => window.location.reload() : onReconnect}
                className="flex items-center gap-1.5 text-xs text-primary hover:text-primary-hover mt-2"
              >
                <RefreshCw className="h-3 w-3" />
                {connectionState === 'unauthenticated' ? 'Refresh page' : 'Retry connection'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
